import React, { type ReactElement, type ReactNode } from 'react';

import { ColumnHeaderCell, type SortDirection } from '../ColumnFilter';
import { useUIColors } from '../../theme';
import { cx, defineRecipe, useRecipe, type Styleable } from '../../variants';

/**
 * EAA-1821: the generic half of a hand-built `<table>` — its own scroll
 * region, an optional sticky header built from `ColumnHeaderCell`, and rows
 * that can be a plain grid or an activatable list — pulled out of the
 * console's audit table so the next screen that cannot use `DataTable` does
 * not re-hand-roll the same scaffolding.
 *
 * Reach for `DataTable` first — it covers sorting, filtering, pagination and
 * row selection over antd's `Table` and is the shorter path for an ordinary
 * list. Reach for `PlainTable` only when a screen needs something antd's
 * column model cannot express: a column that swaps identity per row (the
 * audit table's "Chế độ"/"Đối tượng" split), a domain row tint keyed off data
 * antd never sees, or a filter popover anchored inside the scroll region
 * rather than antd's own portal layer. `PlainTable` renders the markup; it
 * owns no filter state, no domain vocabulary and no copy — everything a
 * caller sees comes in through `columns`/`rows`/`labels`.
 *
 * What stayed in `apps/portal/src/pages/console/audit/AuditTable.tsx` and did
 * NOT move here, because it is specific to that screen and not to tables in
 * general: which columns exist, the credential-mode/target-type column swap,
 * the failed-row tint, the per-cell renderers (actor/target/date/status), and
 * all Vietnamese copy. Those stay app-side and are expressed through this
 * component's `columns`/`rowClassName`/`render` props.
 */

/** One column: a header slot the caller may make sortable and/or give a
 *  filter control, and a `render` that owns the cell body for every row. */
export interface PlainTableColumn<T> {
    key: string;
    label: ReactNode;
    /** CSS width, e.g. `'17%'` — fed into the table's `<colgroup>`. */
    width?: string;
    /** Makes the header label a sort toggle; the click still goes through `sort.onSort`. */
    sortable?: boolean;
    /** The column's own filter control — typically a `ColumnFilterButton` inside a `FilterPopover`. */
    control?: ReactNode;
    render: (row: T) => ReactNode;
    /** Per-cell class hook, so an app can style one column without a descendant selector. */
    cellClassName?: string;
}

/** Which column is sorted, which way, and how the header asks for a change —
 *  grouped so the sort axis stays one prop instead of three. */
export interface PlainTableSort {
    key: string;
    direction: SortDirection;
    onSort: (key: string) => void;
}

export interface PlainTableProps<T> extends Styleable {
    columns: PlainTableColumn<T>[];
    rows: T[];
    rowKey: (row: T) => string;
    sort?: PlainTableSort;
    /** Row click + Enter/Space. Absent ⇒ rows render as plain, non-interactive `<tr>`s. */
    onRowActivate?: (row: T) => void;
    /** App-supplied per-row class — how a caller expresses a row tint (e.g. a
     *  failed-row highlight) WITHOUT this package learning the domain state
     *  behind it. */
    rowClassName?: (row: T) => string | undefined;
    /** Sticky header inside THIS component's own scroll region — see the note below. */
    stickyHeader?: boolean;
    /** Rendered as a single full-width row in place of the body when `rows` is empty. */
    empty?: ReactNode;
}

// EAA-1821: sticky lives on the SCROLL CONTAINER, not the table — `overflow`
// on an ancestor is exactly what breaks `position: sticky`, so a caller that
// wraps this component in its own scrolling div gets a header that scrolls
// away with the rows. `stickyHeader` therefore only works because this
// component owns the scroll region itself; it is not decorative.
export const plainTableScrollRecipe = defineRecipe('plainTableScroll', 'ui-plain-table-scroll', {
    variants: {
        sticky: { yes: 'ui-plain-table-scroll--sticky', no: '' },
    },
    defaultVariants: { sticky: 'no' },
});

// String `yes`/`no` keys, not booleans — see CLAUDE.md: a boolean-keyed axis
// cannot be addressed by the string-keyed override registry.
export const plainTableRowRecipe = defineRecipe('plainTableRow', 'ui-plain-table-row', {
    variants: {
        interactive: { yes: 'ui-plain-table-row--interactive', no: '' },
    },
    defaultVariants: { interactive: 'no' },
});

export function PlainTable<T>({
    columns,
    rows,
    rowKey,
    sort,
    onRowActivate,
    rowClassName,
    stickyHeader = false,
    empty,
    className,
    style,
}: PlainTableProps<T>): ReactElement {
    const colors = useUIColors();
    const scrollClassName = useRecipe(plainTableScrollRecipe, {
        sticky: stickyHeader ? 'yes' : 'no',
    });
    // EAA-1821: resolved once per render, above the `rows.length === 0` early
    // exit further down — `useRecipe` is a hook and must run unconditionally.
    const interactiveRowClassName = useRecipe(plainTableRowRecipe, {
        interactive: onRowActivate ? 'yes' : 'no',
    });

    function handleKeyDown(event: React.KeyboardEvent<HTMLTableRowElement>, row: T): void {
        if (!onRowActivate) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        // Space must not scroll the page while a row has focus.
        event.preventDefault();
        onRowActivate(row);
    }

    return (
        <div
            className={cx('ui-plain-table-scroll', scrollClassName, className)}
            style={{ overflow: 'auto', ...style }}
        >
            <table
                className="ui-plain-table"
                style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}
            >
                {columns.some((column) => column.width) && (
                    <colgroup>
                        {columns.map((column) => (
                            <col key={column.key} style={{ width: column.width }} />
                        ))}
                    </colgroup>
                )}
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <ColumnHeaderCell
                                key={column.key}
                                label={column.label}
                                control={column.control}
                                sticky={stickyHeader}
                                sorted={
                                    column.sortable && sort?.key === column.key
                                        ? sort.direction
                                        : null
                                }
                                onSort={
                                    column.sortable && sort
                                        ? () => sort.onSort(column.key)
                                        : undefined
                                }
                            />
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && empty ? (
                        <tr>
                            <td colSpan={columns.length}>{empty}</td>
                        </tr>
                    ) : (
                        rows.map((row) => {
                            const interactive = Boolean(onRowActivate);
                            return (
                                <tr
                                    key={rowKey(row)}
                                    className={cx(interactiveRowClassName, rowClassName?.(row))}
                                    // EAA-1821: only an activatable row gets `tabIndex`/`role` —
                                    // a non-interactive table must not announce itself as one to
                                    // assistive tech or trap Tab on rows nothing happens to.
                                    role={interactive ? 'button' : undefined}
                                    tabIndex={interactive ? 0 : undefined}
                                    style={{ cursor: interactive ? 'pointer' : undefined }}
                                    onClick={interactive ? () => onRowActivate?.(row) : undefined}
                                    onKeyDown={
                                        interactive ? (event) => handleKeyDown(event, row) : undefined
                                    }
                                >
                                    {columns.map((column) => (
                                        <td
                                            key={column.key}
                                            className={column.cellClassName}
                                            style={{ borderBottom: `1px solid ${colors.divider}` }}
                                        >
                                            {column.render(row)}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default PlainTable;
